import {useMemo} from 'react';
import clsx from 'clsx';
import {Link} from '@shopify/hydrogen';
import {missingClass} from '~/lib/utils';
import {ButtonVariant, ButtonWidth} from '~/lib/types';
import {twMerge} from 'tailwind-merge';
import {ButtonLinkEntity} from '~/types';
import Spinner from './Spinner';

export function Button({
  as = 'button',
  className = '',
  variant = 'primary',
  width = 'auto',
  height,
  size = 'md',
  maxWidth,
  openInNewTab = false,
  entity,
  loading,
  onPress,
  ...props
}: {
  as?: React.ElementType;
  className?: string;
  variant?: ButtonVariant;
  width?: ButtonWidth;
  height?: number | string;
  size?: 'sm' | 'md' | 'lg';
  maxWidth?: string | number;
  openInNewTab?: boolean;
  entity?: Partial<ButtonLinkEntity>;
  loading?: boolean;
  onPress?: (action: Partial<ButtonLinkEntity>) => void;
  [key: string]: any;
}) {
  const isAction = entity?.buttonClickType === 'action';
  const Component = props?.to && !isAction ? Link : as;

  const baseButtonClasses =
    'inline-block rounded-primary font-header text-center select-none' +
    'transition ease-in-out duration-200 hover:opacity-80';

  const variants = {
    primary: `${baseButtonClasses} bg-primary text-contrast hover:text-white`,
    'primary-inverse': `${baseButtonClasses} border-2 border-solid border-black text-black hover:bg-white`,
    secondary: `${baseButtonClasses} bg-contrast text-primary`,
    blue: `${baseButtonClasses} bg-blue-400 text-white hover:text-white`,
    'blue-light': `${baseButtonClasses} bg-blue-500 text-white hover:text-white`,
    'blue-secondary': `${baseButtonClasses} border-2 border-blue-300`,
    gray: `${baseButtonClasses} bg-gray-400 text-black hover:text-white`,
    'gray-light': `${baseButtonClasses} bg-gray-200 text-black`,
  };

  const widths = {
    auto: 'w-auto',
    full: 'w-full',
  };

  const styles = twMerge(
    clsx(
      missingClass(className, 'bg-') && variants[variant],
      missingClass(className, 'w-') &&
        typeof width === 'string' &&
        widths[width],
      className,
      {
        'py-2 px-4 text-sm leading-5.5 tracking-wider font-bold': size === 'sm',
        'py-3.5 px-7.5 text-sm leading-5.5 tracking-wider font-bold':
          size === 'md',
        'py-6 px-20 text-xl font-bold': size === 'lg',
      },
      // Remove the default padding if width is defined
      typeof width === 'number' && 'px-1',
      loading && 'bg-gray-100',
    ),
    className,
  );

  const style = useMemo(() => {
    return {
      maxWidth,
      width: width !== 'full' ? width : undefined,
      height,
    };
  }, [maxWidth, width, height]);

  const additionalProps: Record<string, unknown> = {
    target: openInNewTab ? '_blank' : undefined,
    rel: openInNewTab ? 'noopener noreferrer' : undefined,
  };

  if (isAction) {
    additionalProps.onClick = () => onPress?.(entity);
  }

  const children = loading ? (
    <div className={'w-full flex justify-center items-center'}>
      <Spinner />
    </div>
  ) : (
    props.children
  );

  return (
    <Component className={styles} style={style} {...props} {...additionalProps}>
      {children}
    </Component>
  );
}
